import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FileText,
  Upload,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowRight,
  MessageSquare,
  HelpCircle,
  Download,
  Loader2,
} from 'lucide-react';
import { crmPortalService } from '@/services/api/crm-portal';
import { crmAuth } from '@/services/auth/crm-auth';
import { cn } from '@/lib/utils';
import { UploadDocumentDialog } from '@/components/crm/UploadDocumentDialog';
import type { IntakeDocumentStatus, IntakeDocumentType } from '@/types/intake';

export function CRMDashboardPage() {
  const navigate = useNavigate();
  const client = crmAuth.getClient();

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<IntakeDocumentType | null>(null);
  const [isReupload, setIsReupload] = useState(false);

  // Fetch client progress using CRM portal API
  const { data: progress, isLoading: progressLoading } = useQuery({
    queryKey: ['crm-progress'],
    queryFn: () => crmPortalService.getProgress(),
  });

  // Fetch client documents using CRM portal API
  const { data: documents, isLoading: documentsLoading } = useQuery({
    queryKey: ['crm-documents'],
    queryFn: () => crmPortalService.getDocuments(),
  });

  // Fetch document types using CRM portal API
  const { data: documentTypesData } = useQuery({
    queryKey: ['crm-document-types'],
    queryFn: () => crmPortalService.getDocumentTypes(),
  });

  const documentTypes = documentTypesData?.documentTypes || [];
  const isLoading = progressLoading || documentsLoading;

  const getStatusInfo = (status?: IntakeDocumentStatus) => {
    switch (status) {
      case 'approved':
        return { label: 'Approved', color: 'text-green-500', bgColor: 'bg-green-500/10', icon: CheckCircle2 };
      case 'rejected':
        return { label: 'Rejected', color: 'text-red-500', bgColor: 'bg-red-500/10', icon: AlertCircle };
      case 'extracted':
      case 'validated':
        return { label: 'Under Review', color: 'text-amber-500', bgColor: 'bg-amber-500/10', icon: Clock };
      case 'uploaded':
      case 'processing':
        return { label: 'Processing', color: 'text-blue-500', bgColor: 'bg-blue-500/10', icon: Clock };
      default:
        return { label: 'Pending', color: 'text-muted-foreground', bgColor: 'bg-muted', icon: FileText };
    }
  };

  // Map documents to their types
  const documentMap = new Map(documents?.map(d => [d.documentTypeId, d]) || []);

  // Download state
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleUploadClick = (docType: IntakeDocumentType, reupload: boolean = false) => {
    setSelectedDocType(docType);
    setIsReupload(reupload);
    setUploadDialogOpen(true);
  };

  const handleDownload = async (documentId: string) => {
    setDownloadingId(documentId);
    try {
      const { downloadUrl } = await crmPortalService.getDocumentDownloadUrl(documentId);
      // Open download URL in new tab
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  // Helper to format file size
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper to format date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="space-y-2">
        <h1 className="text-2xl md:text-3xl font-bold">
          Welcome Back{client?.name ? `, ${client.name.split(' ')[0]}` : ''}
        </h1>
        <p className="text-muted-foreground">
          Track your intake progress and upload required documents.
        </p>
      </div>

      {/* Progress Overview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Intake Progress</CardTitle>
          <CardDescription>
            {progress
              ? `${progress.uploaded} of ${progress.totalRequired} required documents uploaded`
              : 'Loading progress...'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-4 w-full" />
          ) : (
            <div className="space-y-2">
              <Progress value={progress?.progressPercent || 0} className="h-3" />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {progress?.progressPercent || 0}% Complete
                </span>
                {progress?.approved === progress?.totalRequired && (progress?.totalRequired ?? 0) > 0 ? (
                  <span className="text-green-500 font-medium flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" />
                    All documents approved
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    {progress?.pending || 0} remaining
                  </span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10">
                <FileText className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{progress?.totalRequired || 0}</p>
                <p className="text-sm text-muted-foreground">Required</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
                <Upload className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{progress?.uploaded || 0}</p>
                <p className="text-sm text-muted-foreground">Uploaded</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500/10">
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{progress?.approved || 0}</p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-500/10">
                <AlertCircle className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{progress?.rejected || 0}</p>
                <p className="text-sm text-muted-foreground">Need Action</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Document Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Document Checklist</CardTitle>
          <CardDescription>Required documents for your intake. Click Upload to submit each document.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : documentTypes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No document types configured yet.</p>
              <p className="text-sm">Please contact support if you believe this is an error.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documentTypes.map((docType) => {
                const uploadedDoc = documentMap.get(docType.id);
                const statusInfo = getStatusInfo(uploadedDoc?.status);
                const StatusIcon = statusInfo.icon;
                const hasUploadedFile = uploadedDoc && uploadedDoc.status !== 'pending';

                return (
                  <div
                    key={docType.id}
                    className={cn(
                      'p-4 rounded-lg border',
                      uploadedDoc?.status === 'rejected' && 'border-red-500/50 bg-red-500/5'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg', statusInfo.bgColor)}>
                          <StatusIcon className={cn('h-5 w-5', statusInfo.color)} />
                        </div>
                        <div>
                          <p className="font-medium">
                            {docType.name}
                            {docType.isRequired && (
                              <span className="ml-2 text-xs text-red-500">*Required</span>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">{docType.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className={cn(statusInfo.bgColor, statusInfo.color)}>
                          {statusInfo.label}
                        </Badge>
                        {(!uploadedDoc || uploadedDoc.status === 'rejected') && (
                          <Button
                            size="sm"
                            variant={uploadedDoc?.status === 'rejected' ? 'destructive' : 'outline'}
                            onClick={() => handleUploadClick(docType, uploadedDoc?.status === 'rejected')}
                          >
                            <Upload className="mr-2 h-4 w-4" />
                            {uploadedDoc?.status === 'rejected' ? 'Re-upload' : 'Upload'}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* File info section for uploaded documents */}
                    {hasUploadedFile && (
                      <div className="mt-3 pt-3 border-t flex items-center justify-between">
                        <div className="flex items-center gap-3 text-sm">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium text-foreground">{uploadedDoc.originalName}</p>
                            <p className="text-muted-foreground">
                              {formatFileSize(uploadedDoc.fileSizeBytes)}
                              {uploadedDoc.uploadedAt && ` • Uploaded ${formatDate(uploadedDoc.uploadedAt)}`}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownload(uploadedDoc.id)}
                          disabled={downloadingId === uploadedDoc.id}
                        >
                          {downloadingId === uploadedDoc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="mr-2 h-4 w-4" />
                              Download
                            </>
                          )}
                        </Button>
                      </div>
                    )}

                    {/* Rejection reason */}
                    {uploadedDoc?.rejectionReason && (
                      <p className="text-sm text-red-500 mt-2">
                        Reason: {uploadedDoc.rejectionReason}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="hover:border-primary/50 transition-colors cursor-pointer" onClick={() => navigate('/crm/chat')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <MessageSquare className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Need Help?</p>
                  <p className="text-sm text-muted-foreground">Chat with our AI assistant</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover:border-primary/50 transition-colors cursor-pointer" onClick={() => navigate('/crm/help')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10">
                  <HelpCircle className="h-6 w-6 text-amber-500" />
                </div>
                <div>
                  <p className="font-medium">FAQs & Help</p>
                  <p className="text-sm text-muted-foreground">Find answers to common questions</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Dialog */}
      <UploadDocumentDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        documentType={selectedDocType}
        isReupload={isReupload}
      />
    </div>
  );
}
